export const CERTIFICATION_TYPE_OTHER = 'other';

export const DRIVER_STATUS = {
  INVITED: 'invited',
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
};

export const DRIVER_STATUS_OPTIONS = [
  { id: DRIVER_STATUS.INVITED, label: 'Invited', badgeClass: 'badge-invited' },
  { id: DRIVER_STATUS.ACTIVE, label: 'Active', badgeClass: 'badge-active' },
  { id: DRIVER_STATUS.SUSPENDED, label: 'Suspended', badgeClass: 'badge-suspended' },
];

export const DRIVER_LICENSE_TYPE_OPTIONS = [
  { id: 'class_a_cdl', label: 'Class A CDL' },
  { id: 'class_b_cdl', label: 'Class B CDL' },
  { id: 'class_c_cdl', label: 'Class C CDL' },
  { id: 'non_cdl', label: 'Non-CDL' },
  { id: 'other', label: 'Other' },
];

export const DRIVER_CERTIFICATION_GROUPED_OPTIONS = [
  {
    category: 'Driving & Endorsements',
    types: [
      { id: 'school_bus', label: 'School Bus Endorsement (S)' },
      { id: 'passenger', label: 'Passenger Endorsement (P)' },
      { id: 'air_brakes', label: 'Air Brakes Endorsement (L)' },
      { id: 'hazmat', label: 'Hazmat Endorsement (H)' },
      { id: 'tanker', label: 'Tank Vehicle Endorsement (N)' },
      { id: 'doubles_triples', label: 'Double/Triple Trailer Endorsement (T)' },
    ],
  },
  {
    category: 'Safety & Emergency',
    types: [
      { id: 'dot_physical', label: 'DOT Physical / Medical Certificate' },
      { id: 'first_aid_cpr', label: 'First Aid / CPR' },
      { id: 'defensive_driving', label: 'Defensive Driving' },
      { id: 'evoc', label: 'Emergency Vehicle Operation (EVOC)' },
      { id: 'fire_extinguisher', label: 'Fire Extinguisher Training' },
    ],
  },
  {
    category: 'Equipment Operation',
    types: [
      { id: 'forklift', label: 'Forklift Operator' },
      { id: 'scissor_lift', label: 'Scissor Lift / Aerial Work Platform' },
      { id: 'boom_lift', label: 'Boom Lift' },
      { id: 'backhoe_excavator', label: 'Backhoe / Excavator' },
      { id: 'skid_steer', label: 'Skid Steer' },
    ],
  },
  {
    category: 'Compliance',
    types: [
      { id: 'osha_10', label: 'OSHA 10' },
      { id: 'osha_30', label: 'OSHA 30' },
    ],
  },
  {
    category: 'Other',
    types: [
      { id: 'other', label: 'Other' },
    ],
  },
];

export const DRIVER_CERTIFICATION_OPTIONS = DRIVER_CERTIFICATION_GROUPED_OPTIONS.flatMap(
  group => group.types,
);

const statusMap = Object.fromEntries(
  DRIVER_STATUS_OPTIONS.map(o => [o.id, o]),
);

const licenseTypeMap = Object.fromEntries(
  DRIVER_LICENSE_TYPE_OPTIONS.map(o => [o.id, o.label]),
);

export const certificationMap = Object.fromEntries(
  DRIVER_CERTIFICATION_OPTIONS.map(o => [o.id, o.label]),
);

export function getDriverStatusLabel(status) {
  return statusMap[status]?.label || status;
}

export function getDriverStatusBadgeClass(status) {
  return statusMap[status]?.badgeClass || '';
}

export function getDriverLicenseTypeLabel(type) {
  return licenseTypeMap[type] || '\u2014';
}

export function getDriverCertificationsLabel(certs) {
  if (!certs || !certs.length) return '\u2014';
  return certs.map((c) => {
    if (typeof c === 'string') return certificationMap[c] || c;
    const type = c.certification_type || c.type;
    if (type === 'other' && c.certification_name) return c.certification_name;
    return certificationMap[type] || type;
  }).join(', ');
}

export const VEHICLE_STATUS_OPTIONS = [
  { id: 'operating', label: 'Operating', color: '#2E933C', badgeClass: 'badge-operating' },
  { id: 'backup', label: 'Backup / Spare', color: '#216093', badgeClass: 'badge-backup' },
  { id: 'maintenance_required', label: 'Maintenance Required', color: '#e9c547', badgeClass: 'badge-maintenance-required' },
  { id: 'needs_repair', label: 'Needs Repair / Down', color: '#E63462', badgeClass: 'badge-needs-repair' },
  { id: 'scrap', label: 'Scrap', color: '#042b48', badgeClass: 'badge-scrap' },
];

const vehicleStatusMap = Object.fromEntries(
  VEHICLE_STATUS_OPTIONS.map(o => [o.id, o]),
);

export function getVehicleStatusLabel(status) {
  return vehicleStatusMap[status]?.label || 'Unknown';
}

export function getVehicleStatusColor(status) {
  return vehicleStatusMap[status]?.color || '#94a3b8';
}

export function getVehicleStatusBadgeClass(status) {
  return vehicleStatusMap[status]?.badgeClass || '';
}

export const VEHICLE_STATUS_SELECT_OPTIONS = VEHICLE_STATUS_OPTIONS.map(o => ({
  id: o.id,
  display_label: o.label,
}));

export const VEHICLE_FUEL_TYPE_OPTIONS = [
  { id: 'gasoline', display_label: 'Gasoline' },
  { id: 'diesel', display_label: 'Diesel' },
  { id: 'electric', display_label: 'Electric' },
  { id: 'hybrid', display_label: 'Hybrid' },
  { id: 'cng', display_label: 'CNG' },
  { id: 'other', display_label: 'Other' },
];

export function getYearOptions() {
  const currentYear = new Date().getFullYear();
  const options = [];
  for (let y = currentYear + 1; y >= 1970; y--) {
    options.push({ id: y, display_label: String(y) });
  }
  return options;
}

// Driver table field configurations.
// DRIVER_TABLE_ALL_FIELDS is every column the field picker offers, in display order.
export const DRIVER_TABLE_ALL_FIELDS = [
  { id: 'name', label: 'Name' },
  { id: 'status', label: 'Status' },
  { id: 'license_type', label: 'License Class' },
  { id: 'license_state', label: 'License State' },
  { id: 'license_number', label: 'License Number' },
  { id: 'license_expiration', label: 'License Exp.' },
  { id: 'assigned_vehicles', label: 'Assigned Vehicles' },
  { id: 'certifications', label: 'Certifications' },
];

// Optional columns: offered in the field picker but not shown until a user selects them.
const DRIVER_TABLE_OPTIONAL_FIELD_IDS = ['license_number'];

export const DRIVER_TABLE_DEFAULT_FIELDS = DRIVER_TABLE_ALL_FIELDS.filter(
  f => !DRIVER_TABLE_OPTIONAL_FIELD_IDS.includes(f.id),
);

// Driver filter select options (use { id, display_label } for InputMultiSelectSearchable)
export const DRIVER_STATUS_SELECT_OPTIONS = DRIVER_STATUS_OPTIONS.map(o => ({
  id: o.id,
  display_label: o.label,
}));

export const DRIVER_LICENSE_TYPE_SELECT_OPTIONS = DRIVER_LICENSE_TYPE_OPTIONS.map(o => ({
  id: o.id,
  display_label: o.label,
}));

export const DRIVER_CERTIFICATION_SELECT_OPTIONS = DRIVER_CERTIFICATION_OPTIONS.map(o => ({
  id: o.id,
  display_label: o.label,
}));

export const DRIVER_LICENSE_EXPIRATION_OPTIONS = [
  { id: 'expired', display_label: 'Expired' },
  { id: '30', display_label: 'Expiring within 30 days' },
  { id: '60', display_label: 'Expiring within 60 days' },
  { id: '90', display_label: 'Expiring within 90 days' },
];

export const DRIVER_CERT_EXPIRATION_OPTIONS = [
  { id: 'expired', display_label: 'Expired' },
  { id: '30', display_label: 'Expiring within 30 days' },
  { id: '60', display_label: 'Expiring within 60 days' },
  { id: '90', display_label: 'Expiring within 90 days' },
];

// Vehicle table fields. As with inspections below, `csvDownloadId` is the name the CSV export
// handler expects where it differs from the table column id. The column id itself stays as is
// because it is persisted in each user's saved column selection.
export const VEHICLE_TABLE_DEFAULT_FIELDS = [
  { id: 'name', label: 'Vehicle' },
  { id: 'location', label: 'Location' },
  { id: 'make', label: 'Manufacturer' },
  { id: 'model', label: 'Model' },
  { id: 'license_plate', label: 'Plate' },
  { id: 'assigned_drivers', label: 'Assigned Drivers', csvDownloadId: 'assigned_driver' },
  { id: 'odometer', label: 'Odometer (mi)' },
  { id: 'last_inspection_date', label: 'Last Inspection' },
  { id: 'status', label: 'Status' },
];

export const VEHICLE_TABLE_ALL_FIELDS = [
  ...VEHICLE_TABLE_DEFAULT_FIELDS,
  { id: 'year', label: 'Year' },
  { id: 'vin', label: 'VIN' },
  { id: 'fuel_type', label: 'Fuel Type' },
  { id: 'reservable', label: 'Reservable' },
  { id: 'registration_expiration', label: 'Registration Expiry' },
  { id: 'insurance_expiration', label: 'Insurance Expiry' },
];

// The selected vehicle columns, named the way the export handler names them, in table order.
export function vehicleCsvExportFields(selectedFieldIds = []) {
  const selected = new Set(selectedFieldIds);
  return VEHICLE_TABLE_ALL_FIELDS
    .filter(f => selected.has(f.id))
    .map(f => f.csvDownloadId || f.id);
}

// Inspection table fields.
// `id` is the view-model property the table renders; `csvDownloadId` is the field name the CSV
// export handler reads off the record. They differ wherever the table shows a computed or
// renamed value, so the export maps through `csvExportFields` below rather than sending ids.
export const INSPECTION_TABLE_DEFAULT_FIELDS = [
  { id: 'templateName', label: 'Inspection Name', sortKey: 'template__name', csvDownloadId: 'template__name' },
  { id: 'vehicleNameSimple', label: 'Vehicle', sortKey: 'vehicle__name', csvDownloadId: 'vehicle__name' },
  { id: 'tripName', label: 'Trip', sortKey: 'trip__title', csvDownloadId: 'trip__title' },
  { id: 'inspection_type', label: 'Type', sortKey: 'inspection_type' },
  { id: 'inspector_name', label: 'Inspector', csvDownloadId: 'inspector__full_name' },
  { id: 'inspection_date', label: 'Due Date', sortKey: 'inspection_date' },
  { id: 'status', label: 'Status', sortKey: 'status' },
  { id: 'result', label: 'Result' },
];

export const INSPECTION_TABLE_ALL_FIELDS = [
  ...INSPECTION_TABLE_DEFAULT_FIELDS,
  { id: 'created', label: 'Created Date', sortKey: 'created' },
  { id: 'odometer', label: 'Odometer', sortKey: 'odometer' },
];

// The columns the user has on screen, named the way the export handler names them, and in the
// order the picker holds rather than the order they were ticked. A field with no explicit
// csvDownloadId exports under its own id, which is already the model field name.
export function inspectionCsvExportFields(selectedFieldIds = []) {
  const selected = new Set(selectedFieldIds);
  return INSPECTION_TABLE_ALL_FIELDS
    .filter(f => selected.has(f.id))
    .map(f => f.csvDownloadId || f.id);
}

// `Inspection.inspection_date` is a DateTimeField, so a bare 'YYYY-MM-DD' body is stored as UTC
// midnight and then rendered back in the browser's timezone — every viewer west of UTC sees the
// previous day, and the edit prefill (which reads the UTC date) disagrees with the table. These
// two convert between the picker's simple date and the instant the user actually meant, local
// midnight, so writing and reading a due date both happen in one timezone.
export function simpleDateToLocalInstant(simpleDate) {
  if (!simpleDate) return null;
  const [year, month, day] = String(simpleDate).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day).toISOString();
}

// The closing bound of a due-date filter. A bare 'YYYY-MM-DD' reaches the API as UTC midnight,
// which sits *before* a due date stored at local midnight anywhere west of UTC — so a row the
// table shows as due Oct 1 drops out of a filter whose end date is Oct 1. Send the last instant
// of the chosen local day instead, so the range covers the whole day the user picked.
export function simpleDateToLocalEndOfDay(simpleDate) {
  if (!simpleDate) return null;
  const [year, month, day] = String(simpleDate).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 23, 59, 59, 999).toISOString();
}

export function localInstantToSimpleDate(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${parsed.getFullYear()}-${month}-${day}`;
}

export const INSPECTION_STATUS_SELECT_OPTIONS = [
  { id: 'pending', display_label: 'Pending' },
  { id: 'in_progress', display_label: 'In Progress' },
  { id: 'completed', display_label: 'Completed' },
  { id: 'canceled', display_label: 'Canceled' },
];

export const INSPECTION_RESULT_OPTIONS = [
  { id: 'passed', display_label: 'Pass' },
  { id: 'failed', display_label: 'Fail' },
];

export const INSPECTION_TYPE_SELECT_OPTIONS = [
  { id: 'pre_trip', display_label: 'Pre-Trip' },
  { id: 'post_trip', display_label: 'Post-Trip' },
  { id: 'adhoc', display_label: 'Ad Hoc' },
];

// Trip status options
export const TRIP_STATUS = {
  PENDING_REVIEW: 'pending_review',
  SCHEDULED: 'scheduled',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  EXPIRED: 'expired',
  DENIED: 'denied',
  CANCELED: 'canceled',
};

export const TRIP_REVIEW_ACTION = {
  APPROVE: 'approved',
  DENY: 'denied',
};

export const TRIP_STATUS_OPTIONS = [
  { id: TRIP_STATUS.PENDING_REVIEW, label: 'Pending Review', badgeClass: 'badge-pending' },
  { id: TRIP_STATUS.SCHEDULED, label: 'Scheduled', badgeClass: 'badge-scheduled' },
  { id: TRIP_STATUS.IN_PROGRESS, label: 'In Progress', badgeClass: 'badge-in-progress' },
  { id: TRIP_STATUS.COMPLETED, label: 'Completed', badgeClass: 'badge-completed' },
  { id: TRIP_STATUS.EXPIRED, label: 'Expired', badgeClass: 'badge-expired' },
  { id: TRIP_STATUS.DENIED, label: 'Denied', badgeClass: 'badge-denied' },
  { id: TRIP_STATUS.CANCELED, label: 'Canceled', badgeClass: 'badge-canceled' },
];

const tripStatusMap = Object.fromEntries(
  TRIP_STATUS_OPTIONS.map(o => [o.id, o]),
);

export function getEffectiveTripStatus(item) {
  return item.trip_status || TRIP_STATUS.SCHEDULED;
}

export function getTripStatusLabel(status) {
  return tripStatusMap[status]?.label || status || 'Scheduled';
}

export function getTripStatusBadgeClass(status) {
  return tripStatusMap[status]?.badgeClass || 'badge-scheduled';
}

const inspectionBadgeStatusMap = {
  pending: { id: 'pending', label: 'Pending', badgeClass: 'badge-pending' },
  in_progress: { id: 'in_progress', label: 'In Progress', badgeClass: 'badge-in-progress' },
  canceled: { id: 'canceled', label: 'Canceled', badgeClass: 'badge-canceled' },
};

// Inspection badge status for trip assignments. Every known status is mapped explicitly so a
// new one cannot silently render as "In Progress"; anything unmapped falls back to its raw value.
export function getInspectionBadgeStatus(inspection, inspectionId) {
  if (!inspectionId) return { id: 'due', label: 'Due', badgeClass: 'badge-due' };
  if (!inspection) return { id: 'pending', label: 'Pending', badgeClass: 'badge-pending' };
  if (inspection.status === 'completed') {
    if (inspection.all_passed === true) return { id: 'pass', label: 'Pass', badgeClass: 'badge-pass' };
    if (inspection.all_passed === false) return { id: 'fail', label: 'Fail', badgeClass: 'badge-fail' };
    return { id: 'complete', label: 'Complete', badgeClass: 'badge-pass' };
  }
  return inspectionBadgeStatusMap[inspection.status]
    || { id: inspection.status, label: inspection.status, badgeClass: 'badge-pending' };
}

export const ACTIVE_TRIP_STATUSES = [
  TRIP_STATUS.PENDING_REVIEW,
  TRIP_STATUS.SCHEDULED,
  TRIP_STATUS.IN_PROGRESS,
];

export const TERMINAL_TRIP_STATUSES = [
  TRIP_STATUS.COMPLETED,
  TRIP_STATUS.EXPIRED,
  TRIP_STATUS.DENIED,
  TRIP_STATUS.CANCELED,
];

export const TRIP_STATUS_SELECT_OPTIONS = TRIP_STATUS_OPTIONS.map(o => ({
  id: o.id,
  display_label: o.label,
}));

export function normalizeVehicleTypesResponse(resp) {
  const results = resp?.results || resp || [];
  return results.map(o => ({
    id: o.value,
    display_label: o.display_label,
  }));
}

// Trip denied reason options
export const TRIP_DENIED_REASON_OPTIONS = [
  { id: 'no_vehicles_available', display_label: 'No vehicles available for that date/time' },
  { id: 'no_drivers_available', display_label: 'No drivers available for that date/time' },
  { id: 'maintenance_scheduled', display_label: 'Maintenance scheduled at that time' },
  { id: 'insufficient_notice', display_label: 'Insufficient notice' },
  { id: 'request_incomplete', display_label: 'Request incomplete or missing information' },
  { id: 'other', display_label: 'Other' },
];

// Trip canceled reason options — requester
export const TRIP_REQUESTER_CANCELED_REASON_OPTIONS = [
  { id: 'trip_no_longer_needed', display_label: 'Trip no longer needed' },
  { id: 'date_or_time_changed', display_label: 'Date or time changed' },
  { id: 'destination_changed', display_label: 'Destination changed' },
  { id: 'insufficient_participants', display_label: 'Insufficient participants' },
  { id: 'safety_or_weather_concerns', display_label: 'Safety or weather concerns' },
  { id: 'other', display_label: 'Other' },
];

// Trip canceled reason options — admin
export const TRIP_ADMIN_CANCELED_REASON_OPTIONS = [
  { id: 'vehicle_no_longer_available', display_label: 'Vehicle no longer available' },
  { id: 'driver_no_longer_available', display_label: 'Driver no longer available' },
  { id: 'trip_conflicts', display_label: 'Trip conflicts with another scheduled trip' },
  { id: 'safety_or_weather_concerns', display_label: 'Safety or weather concerns' },
  { id: 'request_policy_violation', display_label: 'Request policy violation' },
  { id: 'other', display_label: 'Other' },
];

// Inspection template table fields
export const INSPECTION_TEMPLATE_TABLE_FIELDS = [
  { id: 'name', label: 'Name' },
  { id: 'inspection_type', label: 'Type' },
  { id: 'completed_count', label: 'Completed' },
  { id: 'pass_rate', label: 'Pass Rate' },
  { id: 'last_used', label: 'Last Used' },
  { id: 'status', label: 'Status' },
  { id: 'created_by', label: 'Created By' },
];

// Inspection template filter options
export const INSPECTION_TEMPLATE_STATUS_OPTIONS = [
  { id: 'active', display_label: 'Active' },
  { id: 'inactive', display_label: 'Inactive' },
];
